import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import topicIcon1 from '../../assets/images/topics/1.svg';
import topicIcon2 from '../../assets/images/topics/2.png';
import topicIcon3 from '../../assets/images/topics/3.svg';
import topicIcon4 from '../../assets/images/topics/4.svg';
import './question-manager-responsive.css';


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const APTITUDE_QUESTION_API = process.env.REACT_APP_QMANAGER_QUESTION_API || `${API_BASE_URL}/api/admin-app/aptitude-questions/`;

const initialQuestions = [];

const QuestionManagerMain = () => {
    const [questions, setQuestions] = useState(initialQuestions);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const buildAuthHeaders = () => {
        const token = localStorage.getItem('guest_access_token');
        if (!token) {
            return null;
        }

        return {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        };
    };

    const mapApiQuestionToUi = (question) => ({
        id: question.id,
        topic: question.topic,
        prompt: question.prompt,
        difficulty: question.difficulty,
        status: question.status,
        marks: question.marks,
        options: {
            A: question.option_a,
            B: question.option_b,
            C: question.option_c,
            D: question.option_d,
        },
        correctOption: question.correct_option,
    });

    const fetchQuestions = async () => {
        const headers = buildAuthHeaders();
        if (!headers) {
            setErrorMessage('Session expired. Please login again.');
            return;
        }

        setIsLoading(true);
        setErrorMessage('');

        try {
            const response = await fetch(APTITUDE_QUESTION_API, {
                method: 'GET',
                headers,
            });

            const data = await response.json();

            if (!response.ok) {
                setErrorMessage(data?.detail || data?.error || 'Failed to load questions.');
                return;
            }

            const questionRows = Array.isArray(data) ? data : (data?.results || []);
            setQuestions(questionRows.map(mapApiQuestionToUi));
        } catch (error) {
            setErrorMessage('Unable to load questions right now.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchQuestions();
    }, []);

    const stats = useMemo(() => {
        const total = questions.length;
        const published = questions.filter((q) => q.status === 'published').length;
        const drafts = questions.filter((q) => q.status === 'draft').length;
        const submitted = questions.filter((q) => q.status === 'submitted').length;
        const hard = questions.filter((q) => q.difficulty === 'Hard').length;

        return [
            {
                icon: topicIcon1,
                title: 'Total Questions',
                value: total,
                detail: 'All aptitude questions',
            },
            {
                icon: topicIcon2,
                title: 'Published',
                value: published,
                detail: 'Live in active tests',
            },
            {
                icon: topicIcon3,
                title: 'Drafts',
                value: drafts,
                detail: 'Pending review',
            },
            {
                icon: topicIcon4,
                title: 'Hard Level',
                value: hard,
                detail: 'Advanced questions',
            },
        ];
    }, [questions]);

    const handleSubmitQuestion = async (id) => {
        const headers = buildAuthHeaders();
        if (!headers) {
            setErrorMessage('Session expired. Please login again.');
            return;
        }

        try {
            const response = await fetch(`${APTITUDE_QUESTION_API}${id}/`, {
                method: 'PATCH',
                headers,
                body: JSON.stringify({
                    status: 'submitted',
                }),
            });

            if (!response.ok) {
                setErrorMessage('Failed to submit question.');
                return;
            }

            setQuestions((prev) =>
                prev.map((question) =>
                    question.id === id ? { ...question, status: 'submitted' } : question
                )
            );
        } catch (error) {
            setErrorMessage('Failed to submit question.');
        }
    };

    const handleDeleteQuestion = async (id) => {
        const headers = buildAuthHeaders();
        if (!headers) {
            setErrorMessage('Session expired. Please login again.');
            return;
        }

        try {
            const response = await fetch(`${APTITUDE_QUESTION_API}${id}/`, {
                method: 'DELETE',
                headers,
            });

            if (!response.ok) {
                setErrorMessage('Failed to delete question.');
                return;
            }

            setQuestions((prev) => prev.filter((question) => question.id !== id));
        } catch (error) {
            setErrorMessage('Failed to delete question.');
        }
    };

    const getStatusBadgeClass = (status) => {
        if (status === 'published') return 'badge bg-success';
        if (status === 'draft') return 'badge bg-warning text-dark';
        if (status === 'submitted') return 'badge bg-info';
        return 'badge bg-secondary';
    };

    return (
        <>
            <div className="react_populars_topics pt---70 pb---70 question-manager-dashboard">
                <div className="container">
                    <div className="react__title__section react__title__section2">
                        <div className="row align-v question-manager-title-row">
                            <div className="col-md-8">
                                <h2 className="react__tittle">Question Manager</h2>
                                <p className="react__subtitle">Create, review, and maintain aptitude questions for institution tests.</p>
                            </div>
                        </div>
                    </div>

                    <div className="row pt---30 question-manager-stat-grid">
                        {stats.map((card) => (
                            <div className="col-md-6 col-lg-3" key={card.title}>
                                <div className="item__inner">
                                    <div className="icon">
                                        <img src={card.icon} alt={card.title} />
                                    </div>
                                    <div className="react-content">
                                        <h3 className="react-title">{card.title}</h3>
                                        <h4>{card.value}</h4>
                                        <p>{card.detail}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="accordion__area pb---120 question-manager-content">
                <div className="container">
                    <div className="row g-4">
                        {errorMessage ? (
                            <div className="col-lg-12">
                                <div className="alert alert-danger mb-0" role="alert">{errorMessage}</div>
                            </div>
                        ) : null}

                        <div className="col-lg-12">
                            <div className="login-right-form question-manager-table-card" style={{ height: '100%' }}>
                                <div className="login-top d-flex justify-content-between align-items-center flex-wrap gap-2">
                                    <div>
                                        <h3>Question Bank</h3>
                                        <p>Manage your institution-level aptitude questions.</p>
                                    </div>
                                    <Link 
                                        to="/dashboard/institution/question-manager/create?role=question-manager" 
                                        className="react-btn-primary"
                                        style={{
                                            background: '#1e40af',
                                            border: '2px solid #1e3a8a',
                                            padding: '8px 20px',
                                            borderRadius: '8px',
                                            color: '#fff',
                                            fontWeight: '600',
                                            fontSize: '14px',
                                            boxShadow: '0 4px 8px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
                                            textDecoration: 'none',
                                            display: 'inline-block',
                                            textTransform: 'none',
                                            cursor: 'pointer',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        + Create Question
                                    </Link>
                                </div>

                                <div className="table-responsive question-manager-table-wrapper">
                                    <table className="table mb-0 question-manager-table">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Question</th>
                                                <th>Options</th>
                                                <th>Topic</th>
                                                <th>Difficulty</th>
                                                <th>Status</th>
                                                <th>Marks</th>
                                                <th className="text-end" style={{ minWidth: '220px' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {questions.length === 0 ? (
                                                <tr>
                                                    <td colSpan="8" className="text-center py-4">
                                                        {isLoading ? 'Loading questions...' : 'No questions available.'}
                                                    </td>
                                                </tr>
                                            ) : (
                                                questions.map((question) => (
                                                    <tr key={question.id}>
                                                        <td data-label="ID">{question.id}</td>
                                                        <td data-label="Question">{question.prompt}</td>
                                                        <td data-label="Options">
                                                            <ul className="question-manager-options-list">
                                                                <li className={question.correctOption === 'A' ? 'question-manager-correct-option' : ''}>A. {question.options?.A || '-'}</li>
                                                                <li className={question.correctOption === 'B' ? 'question-manager-correct-option' : ''}>B. {question.options?.B || '-'}</li>
                                                                <li className={question.correctOption === 'C' ? 'question-manager-correct-option' : ''}>C. {question.options?.C || '-'}</li>
                                                                <li className={question.correctOption === 'D' ? 'question-manager-correct-option' : ''}>D. {question.options?.D || '-'}</li>
                                                            </ul>
                                                        </td>
                                                        <td data-label="Topic">{question.topic}</td>
                                                        <td data-label="Difficulty">{question.difficulty}</td>
                                                        <td data-label="Status">
                                                            <span className={getStatusBadgeClass(question.status)}>{question.status}</span>
                                                        </td>
                                                        <td data-label="Marks">{question.marks}</td>
                                                        <td data-label="Actions" className="text-end question-manager-actions-cell">
                                                            <div className="question-manager-action-group" style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                                                <Link to={`/dashboard/institution/question-manager/create?id=${question.id}&role=question-manager`} className="btn btn-outline-primary btn-sm question-manager-action-btn">Edit</Link>
                                                                {question.status === 'draft' && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-success btn-sm question-manager-action-btn"
                                                                        onClick={() => handleSubmitQuestion(question.id)}
                                                                    >
                                                                        Submit
                                                                    </button>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-danger btn-sm question-manager-action-btn"
                                                                    onClick={() => handleDeleteQuestion(question.id)}
                                                                >
                                                                    Delete
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>


                    </div>
                </div>
            </div>
        </>
    );
};

export default QuestionManagerMain;
